export const AI_CATEGORIES = ["Photo", "Create", "Analyze", "Documents", "Sell", "Write"];

export const AI_FEATURES = [
  // ── Photo ──────────────────────────────────────────────────────────────────
  {
    id: "quality-check", category: "Photo", requiresImage: true,
    action: "AI photo quality check", title: "Quality Score",
    short: "Score focus, exposure, noise, and overall polish.",
    prompt: "Rate this photo quality from 1–100. Evaluate focus, exposure, noise, color, sharpness, and polish. Return JSON: {score, summary, fixes:[...], strengths:[...]}.",
  },
  {
    id: "photo-description", category: "Photo", requiresImage: true,
    action: "AI photo description", title: "Photo Description",
    short: "Polished, useful description of the image.",
    prompt: "Describe this photo in polished, useful detail: scene, subject, mood, colors, and practical context.",
  },
  {
    id: "lighting", category: "Photo", requiresImage: true,
    action: "AI lighting feedback", title: "Lighting Coach",
    short: "Fix exposure and light issues instantly.",
    prompt: "Analyze lighting and exposure. Give one immediate capture tip and one editing tip. Be concise.",
  },
  {
    id: "composition", category: "Photo", requiresImage: true,
    action: "AI composition feedback", title: "Composition",
    short: "Framing, crop, angle, and background guidance.",
    prompt: "Analyze composition, crop, angle, background clutter, symmetry, and rule-of-thirds. Give specific improvements.",
  },
  {
    id: "best-filter", category: "Photo", requiresImage: true,
    action: "AI best filter recommendation", title: "Best Filter",
    short: "Pick the perfect LensPro filter for this shot.",
    prompt: "Recommend the best filter from: Clean Pro, Portrait Glow, LinkedIn Studio, Cinematic Gold, Teal Orange Film, Vintage Film, Classic B&W, Product White, Food Warm, Travel Vivid, Night Bright, Document Sharp, Soft Skin, Matte Film, High Contrast, Warm Tone, Cool Tone. Explain why in 2 sentences.",
  },
  {
    id: "retake", category: "Photo", requiresImage: true,
    action: "AI retake suggestion", title: "Retake Advice",
    short: "Clear verdict on whether to reshoot — and how.",
    prompt: "Decide clearly if this photo should be retaken. Give a verdict, the top problem, and precise retake instructions.",
  },
  {
    id: "profile-advice", category: "Photo", requiresImage: true,
    action: "AI profile photo advice", title: "Profile Photo",
    short: "Headshot and profile advice for any platform.",
    prompt: "Evaluate as a profile photo. Give advice on crop, expression, lighting, background, and platform fit (LinkedIn, Instagram, Twitter).",
  },
  {
    id: "product-photo", category: "Photo", requiresImage: true,
    action: "AI product photo polish", title: "Product Photo",
    short: "Make product listings look premium and clean.",
    prompt: "Evaluate as a product listing photo. Suggest background, lighting, crop, angle, cleanup, and export ratio.",
  },
  {
    id: "edit-guide", category: "Photo", requiresImage: true,
    action: "AI photo edit guide", title: "Edit Guide",
    short: "Step-by-step editing instructions with values.",
    prompt: "Give a step-by-step photo editing guide: exposure, contrast, color grading, sharpening, and final touches. Be specific with values where possible.",
  },
  {
    id: "color-palette", category: "Photo", requiresImage: true,
    action: "AI color palette extraction", title: "Color Palette",
    short: "Extract dominant colors and suggest a palette name.",
    prompt: "Extract the 5 dominant colors from this image as hex codes. Give each a descriptive name and suggest an overall palette mood. Return JSON: {palette:[{hex, name}], mood, usage}.",
  },

  // ── Create ─────────────────────────────────────────────────────────────────
  {
    id: "caption", category: "Create", requiresImage: false,
    action: "AI caption generator", title: "Captions",
    short: "5 polished captions in different tones.",
    prompt: "Write 5 polished captions in different tones (professional, playful, minimal, inspirational, conversational). If an image is present, base them on it. Return JSON: {captions:[...]}.",
  },
  {
    id: "hashtags", category: "Create", requiresImage: false,
    action: "AI hashtag generator", title: "Hashtags",
    short: "Curated tags — broad, niche, and local.",
    prompt: "Generate hashtags in 3 groups: broad (5), niche (8), intent/local (5). Avoid spam. Return JSON: {broad:[...], niche:[...], local:[...]}.",
  },
  {
    id: "social-post", category: "Create", requiresImage: false,
    action: "AI social media post", title: "Social Post",
    short: "Full hook + body + CTA ready to post.",
    prompt: "Write a complete social media post: hook, body, CTA, and hashtags. Adapt to context. Keep it natural and human.",
  },
  {
    id: "title", category: "Create", requiresImage: false,
    action: "AI photo title generator", title: "Photo Titles",
    short: "12 short premium titles for the shot.",
    prompt: "Generate 12 short, premium photo titles. Use image mood and subject if present. Return JSON: {titles:[...]}.",
  },
  {
    id: "story", category: "Create", requiresImage: true,
    action: "AI story from photo", title: "Story From Photo",
    short: "120-word cinematic micro story.",
    prompt: "Write a tasteful 120-word micro story inspired by this photo. Cinematic and grounded in the image.",
  },
  {
    id: "travel-blurb", category: "Create", requiresImage: false,
    action: "AI travel blurb", title: "Travel Blurb",
    short: "Editorial location copy — short and long versions.",
    prompt: "Write premium travel/editorial copy. Short version (30 words) and long version (100 words). Return JSON: {short, long}.",
  },
  {
    id: "thread-post", category: "Create", requiresImage: false,
    action: "AI thread post", title: "Thread Post",
    short: "5-tweet X/Twitter thread from image or idea.",
    prompt: "Write a 5-tweet thread. Each tweet max 280 chars. Hook on tweet 1, insight in 2–4, strong CTA on 5. Natural voice. Return JSON: {tweets:[...]}.",
  },

  // ── Analyze ────────────────────────────────────────────────────────────────
  {
    id: "what-is-this", category: "Analyze", requiresImage: true,
    action: "What is this?", title: "What Is This?",
    short: "Instantly identify anything in view.",
    prompt: "Answer 'What is this?' for the image. Be practical, concise, and honest about uncertainty.",
  },
  {
    id: "object-id", category: "Analyze", requiresImage: true,
    action: "AI object identification", title: "Object ID",
    short: "Name, use, and details of all visible objects.",
    prompt: "Identify all visible objects. Explain what each is, its likely use, and note any uncertainty.",
  },
  {
    id: "label-explain", category: "Analyze", requiresImage: true,
    action: "AI label explanation", title: "Label Explain",
    short: "Decode labels, packaging, warnings clearly.",
    prompt: "Explain any visible label, packaging, warning, nutrition facts, or sign in plain language.",
  },
  {
    id: "expiry", category: "Analyze", requiresImage: true,
    action: "AI expiry date capture", title: "Expiry & Dates",
    short: "Find all dates, warnings, and serial numbers.",
    prompt: "Look for expiry dates, manufacture dates, warranty info, warning labels, serial numbers. Return only what is visible.",
  },
  {
    id: "business-card", category: "Analyze", requiresImage: true,
    action: "AI business card extraction", title: "Business Card",
    short: "Extract all contact details from a card.",
    prompt: "Extract name, title, company, phone, email, website, address from the business card. Return JSON: {name, title, company, phone, email, website, address, notes}.",
  },
  {
    id: "food-id", category: "Analyze", requiresImage: true,
    action: "AI food identification", title: "Food ID",
    short: "Identify food, calories, and allergens.",
    prompt: "Identify the food(s) visible. Name them, describe what they are, approximate calories if identifiable, common allergens. Return JSON: {items:[{name, description, calories, allergens:[...]}]}.",
  },

  // ── Documents ──────────────────────────────────────────────────────────────
  {
    id: "document-summary", category: "Documents", requiresImage: true,
    action: "AI document summary", title: "Document Summary",
    short: "Key points, parties, dates, and action items.",
    prompt: "Summarize this document. Extract key points, parties, dates, action items, and open questions.",
  },
  {
    id: "document-explain", category: "Documents", requiresImage: true,
    action: "AI document explanation", title: "Explain Document",
    short: "Plain-language explanation — what to check next.",
    prompt: "Explain this document in plain language. Clarify terms and list what the user should check or do next.",
  },
  {
    id: "receipt-summary", category: "Documents", requiresImage: true,
    action: "AI receipt summary", title: "Receipt Summary",
    short: "Merchant, total, tax, items, and category.",
    prompt: "Extract: merchant, date, total, tax, items if visible, payment method, expense category. Mark uncertain values. Return JSON.",
  },
  {
    id: "expense-report", category: "Documents", requiresImage: true,
    action: "AI expense report", title: "Expense Report",
    short: "Turn a receipt into a reimbursement entry.",
    prompt: "Create an expense report entry: merchant, category, date, total, tax, reimbursable note, and a CSV-formatted row.",
  },
  {
    id: "meeting-notes", category: "Documents", requiresImage: true,
    action: "AI whiteboard notes", title: "Whiteboard Notes",
    short: "Extract and organize board or sticky notes.",
    prompt: "Extract and organize visible whiteboard, sticky note, or handwritten notes into bullets and action items.",
  },
  {
    id: "study-helper", category: "Documents", requiresImage: true,
    action: "AI study helper", title: "Study Helper",
    short: "Summary, key terms, and 5 quiz questions.",
    prompt: "Turn visible study material into a summary, 8 key terms, and 5 quiz questions. Return JSON: {summary, terms:[{term, def}], quiz:[{q, a}]}.",
  },

  // ── Sell ───────────────────────────────────────────────────────────────────
  {
    id: "product-description", category: "Sell", requiresImage: false,
    action: "AI product description", title: "Product Description",
    short: "Listing-ready copy with headline and benefits.",
    prompt: "Write a premium product description: headline, 3 benefit bullets, specs to include, buyer fit, and short listing copy. Use image if present.",
  },
  {
    id: "selling-description", category: "Sell", requiresImage: false,
    action: "AI selling copy", title: "Selling Copy",
    short: "Persuasive marketplace listing text.",
    prompt: "Write persuasive marketplace selling copy: title, condition notes, 5 bullet benefits, SEO keywords, and a friendly CTA.",
  },
  {
    id: "marketplace-seo", category: "Sell", requiresImage: false,
    action: "AI marketplace SEO", title: "Marketplace SEO",
    short: "Title options, keywords, tags, and bullets.",
    prompt: "Generate: 3 SEO title options, 10 search keywords, 5 tags, 3 bullet benefits. Return JSON: {titles:[...], keywords:[...], tags:[...], bullets:[...]}.",
  },
  {
    id: "ad-copy", category: "Sell", requiresImage: false,
    action: "AI ad copy", title: "Ad Copy",
    short: "4 ad variants: premium, DR, playful, minimal.",
    prompt: "Write 4 ad copy variants: premium, direct response, playful, and minimalist. Each with a headline and CTA. Return JSON: {ads:[{style, headline, body, cta}]}.",
  },
  {
    id: "price-estimate", category: "Sell", requiresImage: true,
    action: "AI price estimate", title: "Price Estimate",
    short: "Fair market value range and sell tips.",
    prompt: "Estimate fair market value for this item. Give a price range, condition notes based on what's visible, and 2 tips to sell it faster.",
  },

  // ── Write ──────────────────────────────────────────────────────────────────
  {
    id: "report", category: "Write", requiresImage: false,
    action: "AI report generator", title: "Report",
    short: "Summary, findings, risks, and next steps.",
    prompt: "Create a structured report: summary, findings, risks, recommendations, and next actions.",
  },
  {
    id: "improvement-checklist", category: "Write", requiresImage: false,
    action: "AI improvement checklist", title: "Checklist",
    short: "Top 7 prioritized action steps.",
    prompt: "Create a practical improvement checklist. Prioritize top 7 actions. Return JSON: {checklist:[{action, priority}]}.",
  },
  {
    id: "email-draft", category: "Write", requiresImage: false,
    action: "AI email draft", title: "Email Draft",
    short: "Subject line + concise professional body.",
    prompt: "Draft a clear, professional email with subject line and concise body. Return JSON: {subject, body}.",
  },
  {
    id: "notes-to-tasks", category: "Write", requiresImage: false,
    action: "AI notes to tasks", title: "Tasks",
    short: "Extract owners, due dates, and priorities.",
    prompt: "Extract tasks, owners if mentioned, due dates if visible, and priority. Return JSON: {tasks:[{task, owner, due, priority}]}.",
  },
  {
    id: "custom", category: "Write", requiresImage: false,
    action: "Custom AI request", title: "Custom Prompt",
    short: "Ask anything — fully open ended.",
    prompt: "Answer the user's custom request using the provided photo and text context. Be practical and concise.",
  },
];

export const AI_ACTIONS = AI_FEATURES.map((f) => f.action);

export function getAIFeature(id) {
  return AI_FEATURES.find((f) => f.id === id) || AI_FEATURES[0];
}

export async function requestGroqAnalysis({ action, prompt, imageDataUrl, text, modelPreference, provider = "auto" }) {
  if (!navigator.onLine) {
    throw new Error("AI tools require internet. Camera, filters, editor, and local gallery still work offline.");
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 45000);
  try {
    const response = await fetch("/api/groq", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, prompt, imageDataUrl, text, modelPreference, provider }),
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("AI is unavailable. Please try again later.");
      }
      throw new Error(payload.error || "AI request failed. Please try again.");
    }
    return payload;
  } finally {
    clearTimeout(timeout);
  }
}
